/**
 * Cookie Consent Manager
 *
 * Manages user cookie consent preferences with localStorage and database sync.
 * GDPR-compliant consent management for WayShare platform.
 *
 * Storage Strategy:
 * - Primary: localStorage (works for all users, immediate)
 * - Secondary: Database (syncs for logged-in users, cross-device)
 */

import { supabase } from '../components/data/supabaseClient';

// Consent version - increment when cookie policy changes significantly
const CONSENT_VERSION = '1.0';

// Consent expiration period (6 months in milliseconds)
const CONSENT_EXPIRATION_MS = 6 * 30 * 24 * 60 * 60 * 1000;

// Default consent state (essential always true, others false by default)
const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  timestamp: null,
  essential: true,      // Always true (auth, language, theme, maps)
  functional: true,     // Enabled by default (saved locations, future features)
  analytics: false,     // Disabled by default (future Google Analytics)
  marketing: false,     // Disabled by default (future Facebook Pixel)
  lastUpdated: null
};

/**
 * Get current consent state from localStorage
 * @returns {Object|null} Consent object or null if not set
 */
export const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem('cookieConsent');
    if (!stored) return null;

    const consent = JSON.parse(stored);

    // Validate consent structure
    if (!consent.version || !consent.timestamp) {
      console.warn('Invalid consent structure, removing');
      localStorage.removeItem('cookieConsent');
      return null;
    }

    return consent;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Check if consent needs to be refreshed (expired or version mismatch)
 * @returns {boolean} True if consent needs refresh
 */
export const needsConsentRefresh = () => {
  const consent = getStoredConsent();

  if (!consent) return true;

  // Check version mismatch
  if (consent.version !== CONSENT_VERSION) {
    console.log('Consent version mismatch, needs refresh');
    return true;
  }

  // Check expiration (6 months)
  const consentAge = Date.now() - new Date(consent.timestamp).getTime();
  if (consentAge > CONSENT_EXPIRATION_MS) {
    console.log('Consent expired, needs refresh');
    return true;
  }

  return false;
};

/**
 * Check if a specific cookie category is allowed
 * @param {string} category - Cookie category (essential/functional/analytics/marketing)
 * @returns {boolean} True if category is allowed
 */
export const canUseCategory = (category) => {
  const consent = getStoredConsent();

  // No consent = only essential
  if (!consent) {
    return category === 'essential';
  }

  // Essential is always allowed
  if (category === 'essential') return true;

  // Check specific category
  return consent[category] === true;
};

/**
 * Update consent preferences
 * @param {Object} preferences - Consent preferences {functional, analytics, marketing}
 * @param {Object} user - Optional current user object for database sync
 * @returns {Promise<boolean>} Success status
 */
export const updateConsent = async (preferences, user = null) => {
  try {
    const now = new Date().toISOString();

    const consent = {
      version: CONSENT_VERSION,
      timestamp: now,
      essential: true, // Always true
      functional: preferences.functional !== undefined ? preferences.functional : DEFAULT_CONSENT.functional,
      analytics: preferences.analytics !== undefined ? preferences.analytics : DEFAULT_CONSENT.analytics,
      marketing: preferences.marketing !== undefined ? preferences.marketing : DEFAULT_CONSENT.marketing,
      lastUpdated: now
    };

    // Save to localStorage (primary storage)
    localStorage.setItem('cookieConsent', JSON.stringify(consent));

    console.log('Cookie consent updated:', consent);

    // Sync to database if user is logged in
    if (user && user.id) {
      await syncConsentToProfile(user.id, consent);
    }

    return true;
  } catch (error) {
    console.error('Error updating cookie consent:', error);
    return false;
  }
};

/**
 * Accept all cookie categories
 * @param {Object} user - Optional current user object for database sync
 * @returns {Promise<boolean>} Success status
 */
export const acceptAllCookies = async (user = null) => {
  return await updateConsent({
    functional: true,
    analytics: true,
    marketing: true
  }, user);
};

/**
 * Reject optional cookies (keep only essential)
 * @param {Object} user - Optional current user object for database sync
 * @returns {Promise<boolean>} Success status
 */
export const rejectOptionalCookies = async (user = null) => {
  return await updateConsent({
    functional: false,
    analytics: false,
    marketing: false
  }, user);
};

/**
 * Sync consent to database (for logged-in users)
 * @param {string} userId - Supabase user ID
 * @param {Object} consent - Consent object to sync
 * @returns {Promise<boolean>} Success status
 */
export const syncConsentToProfile = async (userId, consent = null) => {
  try {
    // Use provided consent or get from localStorage
    const consentToSync = consent || getStoredConsent();

    if (!consentToSync) {
      console.warn('No consent to sync to database');
      return false;
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        cookie_consent_preferences: consentToSync,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) {
      console.error('Error syncing consent to database:', error);
      return false;
    }

    console.log('Consent synced to database for user:', userId);
    return true;
  } catch (error) {
    console.error('Error in syncConsentToProfile:', error);
    return false;
  }
};

/**
 * Load consent from database and sync to localStorage
 * Useful when user logs in from new device
 * @param {string} userId - Supabase user ID
 * @returns {Promise<Object|null>} Consent object or null
 */
export const loadConsentFromProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('cookie_consent_preferences')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error loading consent from database:', error);
      return null;
    }

    if (!data || !data.cookie_consent_preferences) {
      console.log('No consent found in database for user');
      return null;
    }

    const dbConsent = data.cookie_consent_preferences;
    const localConsent = getStoredConsent();

    // Compare timestamps - use newer consent
    if (localConsent && localConsent.timestamp) {
      const localTimestamp = new Date(localConsent.timestamp).getTime();
      const dbTimestamp = new Date(dbConsent.timestamp).getTime();

      if (localTimestamp > dbTimestamp) {
        console.log('Local consent is newer, syncing to database');
        await syncConsentToProfile(userId, localConsent);
        return localConsent;
      }
    }

    // Database consent is newer or local doesn't exist
    console.log('Using consent from database');
    localStorage.setItem('cookieConsent', JSON.stringify(dbConsent));
    return dbConsent;
  } catch (error) {
    console.error('Error in loadConsentFromProfile:', error);
    return null;
  }
};

/**
 * Clear all consent data (for "Clear All Cookies" feature)
 * @param {Object} user - Optional current user object for database sync
 * @returns {Promise<boolean>} Success status
 */
export const clearAllConsent = async (user = null) => {
  try {
    // Remove from localStorage
    localStorage.removeItem('cookieConsent');

    // Clear from database if user is logged in
    if (user && user.id) {
      const { error } = await supabase
        .from('profiles')
        .update({
          cookie_consent_preferences: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error clearing consent from database:', error);
        return false;
      }
    }

    console.log('All consent data cleared');
    return true;
  } catch (error) {
    console.error('Error clearing consent:', error);
    return false;
  }
};

/**
 * Get consent summary for display (e.g., in Settings)
 * @returns {Object} Consent summary with counts and status
 */
export const getConsentSummary = () => {
  const consent = getStoredConsent();

  if (!consent) {
    return {
      hasConsent: false,
      needsRefresh: true,
      enabledCategories: ['essential'],
      enabledCount: 1,
      totalCount: 4,
      lastUpdated: null
    };
  }

  const enabledCategories = ['essential']; // Always included
  if (consent.functional) enabledCategories.push('functional');
  if (consent.analytics) enabledCategories.push('analytics');
  if (consent.marketing) enabledCategories.push('marketing');

  return {
    hasConsent: true,
    needsRefresh: needsConsentRefresh(),
    enabledCategories,
    enabledCount: enabledCategories.length,
    totalCount: 4,
    lastUpdated: consent.lastUpdated
  };
};

/**
 * Initialize consent on app load (call this when user logs in)
 * @param {Object} user - Current user object
 * @returns {Promise<void>}
 */
export const initializeConsentForUser = async (user) => {
  if (!user || !user.id) return;

  try {
    const localConsent = getStoredConsent();
    const dbConsent = await loadConsentFromProfile(user.id);

    // If neither exists, user will see banner
    if (!localConsent && !dbConsent) {
      console.log('No consent found, user will see banner');
      return;
    }

    // Sync is handled in loadConsentFromProfile
    console.log('Consent initialized for user');
  } catch (error) {
    console.error('Error initializing consent for user:', error);
  }
};

// Export default object with all functions
export default {
  getStoredConsent,
  needsConsentRefresh,
  canUseCategory,
  updateConsent,
  acceptAllCookies,
  rejectOptionalCookies,
  syncConsentToProfile,
  loadConsentFromProfile,
  clearAllConsent,
  getConsentSummary,
  initializeConsentForUser,
  CONSENT_VERSION
};
